import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Mail, MessageCircle, Phone } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@stockline/ui/components/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@stockline/ui/components/tooltip";
import { useWhatsappChats, useSmsChats } from "@stockline/web";
import { useEmailSummaries } from "../hooks/useEmailSummaries";
import { SourceIconCluster, type SourceIconChannel } from "./SourceIconCluster";
import { formatDateLocale } from "../utils/dateUtils";
import { cn } from "@/lib/utils";

export type LinkedSourceChannel = "email" | "whatsapp" | "sms";

export interface LinkedSourceEntry {
  channel: LinkedSourceChannel;
  /** Backend chat / email id passed to chatPanel.openPanel. */
  chatId: string;
  /** Primary line, e.g. sender / chat name / phone number. */
  primary: string;
  /** Secondary line, e.g. subject / last message preview. */
  secondary?: string;
  /** ISO date used for display + sorting; appended to the row. */
  date?: string;
  /** Marked as the pre-order's primary source (highlighted with a badge). */
  isPrimary?: boolean;
}

interface LinkedSourcesPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entries: LinkedSourceEntry[];
  channels: SourceIconChannel[];
  /** Hover class applied to the cluster trigger button. */
  triggerHoverClass?: string;
  triggerLabel: string;
  /** Linked-item count badge on the cluster trigger; omitted hides it. */
  totalBadge?: number;
  /**
   * Id currently rendered in the chat panel for this PO — the matching row
   * is rendered as visually selected (greyed) and focused on popover open.
   */
  currentChatId?: string;
  /**
   * Side-effect to run when the trigger is clicked, in addition to Radix
   * toggling the popover. Optional — leave unset when the trigger should
   * only open the dropdown (no chat-panel pivot, etc.).
   */
  onTriggerClick?: () => void;
  onSelect: (entry: LinkedSourceEntry) => void;
  /** Trigger size, forwarded to SourceIconCluster. Defaults to "sm". */
  size?: "sm" | "md";
}

const CHANNEL_ORDER: LinkedSourceChannel[] = ["email", "whatsapp", "sms"];

const CHANNEL_HEADERS: Record<
  LinkedSourceChannel,
  { icon: typeof Mail; defaultLabel: string; key: string }
> = {
  email: {
    icon: Mail,
    defaultLabel: "Emails",
    key: "ordersRecap.linkedSourcesEmails",
  },
  whatsapp: {
    icon: MessageCircle,
    defaultLabel: "WhatsApp",
    key: "ordersRecap.linkedSourcesWhatsapp",
  },
  sms: {
    icon: Phone,
    defaultLabel: "SMS",
    key: "ordersRecap.linkedSourcesSms",
  },
};

export function LinkedSourcesPopover({
  open,
  onOpenChange,
  entries,
  channels,
  triggerHoverClass,
  triggerLabel,
  totalBadge,
  currentChatId,
  onTriggerClick,
  onSelect,
  size = "sm",
}: Readonly<LinkedSourcesPopoverProps>) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <SourceIconCluster
                testId="preorder-source-button"
                channels={channels}
                hoverClass={triggerHoverClass}
                badge={totalBadge}
                ariaLabel={triggerLabel}
                onClick={(e) => {
                  // The cluster may sit inside a click-to-navigate recap row;
                  // toggling the popover must not also trigger row nav.
                  e.stopPropagation();
                  onTriggerClick?.();
                }}
                size={size}
              />
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            {triggerLabel}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent
        align="end"
        side="bottom"
        // Re-anchor every frame so the popover tracks the trigger during the
        // chat-panel slide-in (the layout shift pushes the source icon left
        // and the default `optimized` strategy only updates on scroll /
        // resize, leaving the popover lagging behind for ~200ms).
        updatePositionStrategy="always"
        className="p-1 w-80 max-w-[90vw]"
        data-testid="preorder-source-picker"
        onClick={(e) => {
          // Radix portals the content out of the DOM, but React events still
          // bubble through the component tree to the recap row's onClick.
          // Catch every in-popover click (entry buttons + chrome) here so
          // picking a source never doubles as row → /recap navigation.
          e.stopPropagation();
        }}
        onOpenAutoFocus={(e) => {
          if (!currentChatId) return;
          e.preventDefault();
          const root = e.currentTarget as HTMLElement;
          const target = root.querySelector<HTMLButtonElement>(
            `[data-source-chat-id="${currentChatId}"]`,
          );
          target?.focus();
        }}
      >
        {/* Mounted only while popover is open — Radix unmounts content on close,
            so subscribing to the offline chat / email stores happens
            once-per-open instead of once-per-recap-row. */}
        <PopoverBody
          entries={entries}
          currentChatId={currentChatId}
          onSelect={onSelect}
        />
      </PopoverContent>
    </Popover>
  );
}

interface PopoverBodyProps {
  entries: LinkedSourceEntry[];
  currentChatId?: string;
  onSelect: (entry: LinkedSourceEntry) => void;
}

function PopoverBody({
  entries,
  currentChatId,
  onSelect,
}: Readonly<PopoverBodyProps>) {
  const { t } = useTranslation();
  const enrichedEntries = useEnrichedEntries(entries);

  const grouped = CHANNEL_ORDER.map((channel) => ({
    channel,
    items: enrichedEntries.filter((e) => e.channel === channel),
  })).filter((g) => g.items.length > 0);

  return (
    <>
      {grouped.map((group) => (
        <ChannelSection
          key={group.channel}
          channel={group.channel}
          items={group.items}
          currentChatId={currentChatId}
          t={t}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}

interface ChannelSectionProps {
  channel: LinkedSourceChannel;
  items: LinkedSourceEntry[];
  currentChatId?: string;
  t: TFunction;
  onSelect: (entry: LinkedSourceEntry) => void;
}

const CHANNEL_HEADER_BG: Record<LinkedSourceChannel, string> = {
  email: "bg-blue-50/70 text-blue-800 border-blue-200",
  whatsapp: "bg-green-50/70 text-green-800 border-green-200",
  sms: "bg-purple-50/70 text-purple-800 border-purple-200",
};

function ChannelSection({
  channel,
  items,
  currentChatId,
  t,
  onSelect,
}: Readonly<ChannelSectionProps>) {
  const meta = CHANNEL_HEADERS[channel];
  const HeaderIcon = meta.icon;
  return (
    <div
      data-testid={`preorder-source-section-${channel}`}
      className="mb-1 last:mb-0"
    >
      <div
        className={cn(
          "px-2 py-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide rounded border-b",
          CHANNEL_HEADER_BG[channel],
        )}
      >
        <HeaderIcon className="h-3.5 w-3.5" aria-hidden="true" />
        <span className="flex-1">
          {t(meta.key, { defaultValue: meta.defaultLabel })}
        </span>
        <span className="font-semibold tabular-nums opacity-70">
          {items.length}
        </span>
      </div>
      <ul className="max-h-72 overflow-auto py-0.5">
        {items.map((entry) => (
          <LinkedSourceItem
            key={`${entry.channel}:${entry.chatId}`}
            entry={entry}
            isCurrent={entry.chatId === currentChatId}
            onSelect={onSelect}
          />
        ))}
      </ul>
    </div>
  );
}

interface LinkedSourceItemProps {
  entry: LinkedSourceEntry;
  isCurrent: boolean;
  onSelect: (entry: LinkedSourceEntry) => void;
}

/**
 * Resolves display names for the popover's linked-source entries — all three
 * channels from the offline-first store: WhatsApp / SMS chats via the
 * `@stockline/web` reactive Dexie hooks, emails via {@link useEmailSummaries}.
 *
 * Reading from the offline store keeps every channel instant. The legacy
 * `@/query/hooks` WhatsApp/SMS hooks did a full REST fetch on first open,
 * which left chat rows on their placeholder label for several seconds while
 * email — already on the offline store — resolved immediately.
 *
 * The base `entries` carry placeholder names ("WhatsApp chat" / "SMS chat" /
 * "Email message") for un-enriched linked sources so per-row PreOrderHeader
 * rendering stays cheap. This hook is only called from PopoverBody, which
 * Radix mounts on open and unmounts on close.
 *
 * Only the sender / chat name is surfaced — the last-message preview is
 * intentionally omitted from the popover rows.
 */
function useEnrichedEntries(entries: LinkedSourceEntry[]): LinkedSourceEntry[] {
  const whatsappChats = useWhatsappChats();
  const smsChats = useSmsChats();

  const emailIds = useMemo(
    () => entries.filter((e) => e.channel === "email").map((e) => e.chatId),
    [entries],
  );
  const emailSummaries = useEmailSummaries(emailIds);

  const whatsappById = useMemo(
    () => new Map(whatsappChats.map((c) => [c.id, c])),
    [whatsappChats],
  );
  const smsById = useMemo(
    () => new Map(smsChats.map((c) => [c.id, c])),
    [smsChats],
  );

  return useMemo(() => {
    return entries.map((e) => {
      if (e.channel === "email") {
        const email = emailSummaries.get(e.chatId);
        if (!email) return e;
        return {
          ...e,
          primary: email.fromAddress || e.primary,
          secondary: email.emailSubject || e.secondary,
          date: email.emailDate ?? e.date,
        };
      }
      if (e.channel === "whatsapp") {
        const chat = whatsappById.get(e.chatId);
        if (!chat) return e;
        return {
          ...e,
          primary: chat.name || e.primary,
          date: chat.lastMessageDate ?? e.date,
        };
      }
      if (e.channel === "sms") {
        const chat = smsById.get(e.chatId);
        if (!chat) return e;
        return {
          ...e,
          primary: chat.displayName || chat.phoneNumber || e.primary,
          date: chat.lastMessageDate ?? e.date,
        };
      }
      return e;
    });
  }, [entries, whatsappById, smsById, emailSummaries]);
}

function LinkedSourceItem({
  entry,
  isCurrent,
  onSelect,
}: Readonly<LinkedSourceItemProps>) {
  const dateStr = entry.date
    ? formatDateLocale(
        new Date(entry.date.endsWith("Z") ? entry.date : entry.date + "Z"),
      )
    : "";
  return (
    <li>
      <button
        type="button"
        data-testid="preorder-source-picker-item"
        data-source-chat-id={entry.chatId}
        data-current={isCurrent || undefined}
        aria-current={isCurrent ? "true" : undefined}
        onClick={() => onSelect(entry)}
        className={cn(
          "w-full text-left px-2 py-1.5 rounded hover:bg-gray-100 focus:bg-gray-100 focus:outline-none",
          isCurrent && "bg-gray-200 hover:bg-gray-200 focus:bg-gray-200",
        )}
      >
        <div className="flex items-center gap-1 text-[11px] text-gray-500">
          <span className="truncate flex-1">{entry.primary}</span>
          {dateStr && (
            <span className="text-gray-400 tabular-nums">{dateStr}</span>
          )}
        </div>
        {entry.secondary && (
          <div className="text-xs text-gray-800 truncate">
            {entry.secondary}
          </div>
        )}
      </button>
    </li>
  );
}
